import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useGetPricingPlanAdjustment } from 'src/features/pricingPlan/api/adjustments/useGetPricingPlanAdjustment';
import AdjustmentForm from 'src/features/pricingPlan/components/form/adjustments/AdjustmentForm';
import {
    pricingPlanPeriodAdjustmentFormSchema,
    PricingPlanPeriodAdjustmentFormSchemaValues,
} from 'src/features/pricingPlan/utils/formSchema';
import { PricingPlanPeriodAdjustment } from 'src/features/pricingPlan/utils/types';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { useParking } from 'src/providers/ParkingProvider';

interface AdjustmentFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedAdjustment?: PricingPlanPeriodAdjustment;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: PricingPlanPeriodAdjustmentFormSchemaValues,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
}

function AdjustmentFormModal<TData>({
    selectedAdjustment,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: AdjustmentFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { planId = '' } = useParams();
    const { parkingId } = useParking();

    const { data: pricingPlanAdjustment } = useGetPricingPlanAdjustment({
        planId,
        parkingId,
        adjustmentId: selectedAdjustment?.id ?? '',
        config: { enabled: !!selectedAdjustment?.id && !!planId && isModalOpen },
    });

    const onError = useOnError();

    const defaultValues = React.useMemo(() => {
        return {
            payment_type: pricingPlanAdjustment?.payment_type ?? undefined,
            valid_from: pricingPlanAdjustment?.valid_from ?? undefined,
            valid_to: pricingPlanAdjustment?.valid_to ?? null,
            day_of_week: pricingPlanAdjustment?.day_of_week ?? null,
            from_time: pricingPlanAdjustment?.from_time ?? null,
            to_time: pricingPlanAdjustment?.to_time ?? null,
            adjustment_percent: pricingPlanAdjustment?.adjustment_percent
                ? (Number(pricingPlanAdjustment?.adjustment_percent) * 100).toFixed(2)
                : '',
            adjustment_amount: pricingPlanAdjustment?.adjustment_amount ?? null,
        }
    }, [pricingPlanAdjustment]);

    const form = useForm<PricingPlanPeriodAdjustmentFormSchemaValues>({
        resolver: zodResolver(pricingPlanPeriodAdjustmentFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, planId, pricingPlanAdjustment, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset(defaultValues);

        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: PricingPlanPeriodAdjustmentFormSchemaValues) => {
            const numericPercent = parseFloat(values.adjustment_percent ?? '');
            const adjustedPercent =
                !isNaN(numericPercent) && values.adjustment_percent !== '' ? (numericPercent / 100).toFixed(4) : null;

            const payload = {
                ...values,
                adjustment_percent: adjustedPercent,
            };
            mutate(
                { ...payload },
                () => {
                    setIsModalOpen(false);
                    form.reset(defaultValues);
                },
                (error) => onError(error, form.setError),
            );
        },
        [mutate, onError, form, setIsModalOpen, defaultValues],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={className}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <AdjustmentForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default AdjustmentFormModal;
